import {
  apiErrorFromResponse,
  CSRF_HEADER,
  ensureCsrfCookie,
  readCsrfToken,
} from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiOrigin, fetchWithSessionActivity } from '@/lib/api'
import { useEffectiveGrants } from '@/lib/me'

/** The grant `NotificationPolicy` checks on every `/core/notifications` endpoint. */
export const NOTIFICATION_VIEW_PERMISSION = 'notification.view'

export interface NotificationRecord {
  id: string
  event: string
  payload: Record<string, unknown>
  deep_link: string
  /** The web screen of the record (NotificationDeepLink::forWeb); absent in a response cached before it existed. */
  web_link?: string
  read_at: string | null
  created_at: string
}

export interface NotificationPreference {
  id: string
  event: string
  in_app: boolean
  email: boolean
  push: boolean
}

interface Envelope<T> {
  data: T
}
interface Page<T> {
  data: T[]
  meta?: { current_page?: number; last_page?: number }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (init?.method && init.method !== 'GET') {
    await ensureCsrfCookie(apiOrigin)
  }
  const token = readCsrfToken()
  const response = await fetchWithSessionActivity(`${apiOrigin}/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { [CSRF_HEADER]: token } : {}),
      ...init?.headers,
    },
  })
  if (!response.ok) throw await apiErrorFromResponse(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export function useNotifications(page: number) {
  const { can } = useEffectiveGrants()
  return useQuery({
    queryKey: ['core', 'notifications', page],
    // A role without the grant gets a 403 here, so it never asks (and never polls).
    enabled: can(NOTIFICATION_VIEW_PERMISSION),
    // No push channel yet (WP01 decision D2): a minute's poll is how an
    // "incoming transfer" or "count rejected" notice arrives without a reload.
    refetchInterval: 60_000,
    queryFn: async () =>
      (await request<Page<NotificationRecord>>(`/core/notifications?page=${page}`)).data,
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['core', 'notification-preferences'],
    queryFn: async () =>
      (await request<Envelope<NotificationPreference[]>>('/core/me/notification-preferences')).data,
  })
}

export function useNotificationActions() {
  const client = useQueryClient()
  const refresh = () => void client.invalidateQueries({ queryKey: ['core', 'notifications'] })
  const markRead = useMutation({
    mutationFn: (id: string) => request(`/core/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: refresh,
  })
  const markAllRead = useMutation({
    mutationFn: () => request('/core/notifications/read-all', { method: 'POST' }),
    onSuccess: refresh,
  })
  const savePreferences = useMutation({
    mutationFn: (
      preferences: Array<Pick<NotificationPreference, 'event' | 'in_app' | 'email' | 'push'>>,
    ) =>
      request('/core/me/notification-preferences', {
        method: 'PUT',
        body: JSON.stringify({ preferences }),
      }),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: ['core', 'notification-preferences'] }),
  })
  return { markRead, markAllRead, savePreferences }
}
